/**
 * トースト通知フック + ToastProvider
 *
 * トースト通知の表示・管理を Context で提供する。
 * api:forbidden カスタムイベントを自動リッスンしてエラートースト表示。
 *
 * SPEC 15.2 準拠
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import type { Toast, ToastType } from '@/types';

// ==============================
// 定数
// ==============================

/** デフォルトの自動消去時間 (ミリ秒) */
const DEFAULT_DURATION_MS = 5000;

// ==============================
// Context 定義
// ==============================

/**
 * 構造化トーストの表示内容 (notification-banner-readability-20260820)。
 *
 * 指定したときだけ ToastContainer が「見出し + プレビュー + 行動導線」の 2 段描画に
 * 切り替わる。**未指定が既定**で、その場合は従来どおり `message` 1 行として描画される。
 */
export interface ToastStructuredContent {
  /** 見出し行 (本文より視覚的に強く出す) */
  title: string;
  /** 本文プレビュー。切り詰めずに渡す (行数は CSS の line-clamp が抑える) */
  preview?: string;
  /** 行動導線のラベル。遷移先は `targetPath` が担う */
  actionLabel?: string;
}

interface ToastContextValue {
  /** 現在表示中のトースト一覧 */
  toasts: Toast[];
  /**
   * トーストを表示する
   * @param targetPath 指定するとトースト本文クリックで該当パスへ遷移する (新着通知の案件遷移用)
   * @param structured 指定すると見出し + プレビュー + 行動導線の構造化描画になる
   */
  showToast(
    type: ToastType,
    message: string,
    duration?: number,
    targetPath?: string,
    structured?: ToastStructuredContent,
  ): void;
  /** トーストを手動で削除する */
  removeToast(id: string): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// ==============================
// ToastProvider
// ==============================

interface ToastProviderProps {
  children: ReactNode;
}

/** トースト通知コンテキストプロバイダー */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // タイマー管理用 (クリーンアップのため)
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  /** トーストを削除する */
  const removeToast = useCallback((id: string) => {
    // タイマーをクリア
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /** トーストを表示する */
  const showToast = useCallback(
    (
      type: ToastType,
      message: string,
      duration?: number,
      targetPath?: string,
      structured?: ToastStructuredContent,
    ) => {
      const id = crypto.randomUUID();
      const durationMs = duration ?? DEFAULT_DURATION_MS;

      // structured 未指定なら title / preview / actionLabel は undefined のままにする
      // (欠落 = 従来描画。空文字を入れると構造化扱いになってしまうため spread で足す)。
      const toast: Toast = {
        id,
        type,
        message,
        duration: durationMs,
        targetPath,
        ...(structured ?? {}),
      };
      setToasts((prev) => [...prev, toast]);

      // 自動消去タイマー
      const timer = setTimeout(() => {
        timersRef.current.delete(id);
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, durationMs);
      timersRef.current.set(id, timer);
    },
    [],
  );

  // api:forbidden カスタムイベントをリッスンしてエラートースト表示
  useEffect(() => {
    const handleForbidden = () => {
      showToast('error', 'アクセス権限がありません');
    };
    window.addEventListener('api:forbidden', handleForbidden);
    return () => {
      window.removeEventListener('api:forbidden', handleForbidden);
    };
  }, [showToast]);

  // アンマウント時に全タイマーをクリア
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

// ==============================
// useToast フック
// ==============================

/** トースト通知コンテキストから操作を取得する */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast は ToastProvider 内で使用してください');
  }
  return ctx;
}
